/**
 * Contexto de la visita: qué restaurante y desde qué mesa.
 *
 * El slug viene del primer segmento de la ruta (`/cartazo`) porque cada local
 * necesita una URL propia y compartible. La mesa llega por `?mesa=12`, que es lo
 * que inyecta el resolutor de QR del servidor.
 */

export type VisitContext = {
  slug: string | null
  tableLabel: string | null
  /**
   * Plato al que abrir directamente, por su slug.
   *
   * Lo usa el enlace "Ver en la carta" del editor. Sin esto, revisar un
   * cambio obligaba a abrir la portada y navegar a mano hasta el plato — en
   * una carta de ochenta, eso es no comprobarlo.
   *
   * Va por slug y no por id: es lo que ya identifica al plato de forma única
   * dentro del local, y una URL con un UUID no la reconoce nadie.
   */
  productSlug: string | null
}

export function readContext(): VisitContext {
  if (typeof window === 'undefined') {
    return { slug: null, tableLabel: null, productSlug: null }
  }

  const FORMA = /^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/

  /*
   * El local puede venir en el SUBDOMINIO o en la ruta.
   *
   * En produccion cada carta vive en `<local>.carta.midominio`, que es lo que
   * permite un certificado y una PWA instalable por restaurante. En desarrollo
   * hay un solo servidor y el local va en la ruta: `localhost:5173/juan-cantina`.
   *
   * Leer solo la ruta hacia que en produccion NO se encontrara ningun local: la
   * carta cargaba y mostraba "Carta no encontrada" sobre un dominio correcto.
   *
   * El subdominio manda: si el navegador ya llego por el, discutirlo con la ruta
   * solo puede dar una carta que no corresponde al dominio que se ve arriba.
   */
  const [subdominio, marca] = window.location.hostname.split('.')
  const porDominio = marca === 'carta' && subdominio && FORMA.test(subdominio) ? subdominio : null

  const segments = window.location.pathname.split('/').filter(Boolean)
  const candidate = segments[0] ?? null

  // Solo se acepta un slug con la forma que valida el contrato. Sin esto,
  // cualquier ruta accidental provocaría una petición inútil a la API.
  const slug = porDominio ?? (candidate && FORMA.test(candidate) ? candidate : null)

  const params = new URLSearchParams(window.location.search)
  const raw = params.get('mesa')?.trim() ?? null

  // Se acota la longitud: la etiqueta acaba dentro de un mensaje de WhatsApp y
  // no queremos que un parámetro manipulado inyecte texto arbitrario en él.
  const tableLabel = raw && raw.length > 0 && raw.length <= 40 ? raw : null

  // La misma forma que valida el contrato para un slug, por el mismo motivo:
  // un parámetro cualquiera no debe llegar a buscarse en el índice.
  const plato = params.get('plato')?.trim() ?? null
  const productSlug = plato && /^[a-z0-9][a-z0-9-]{0,58}[a-z0-9]$/.test(plato) ? plato : null

  return { slug, tableLabel, productSlug }
}
